/**
 * Team : projection UI d'une équipe backend (FinOps — regroupement de
 * membres pour ventiler les coûts). Appartenance N-N : un membre peut
 * appartenir à plusieurs équipes.
 */
/**
 * ProviderQuota : plafond mensuel d'un fournisseur (une ProviderKey) — tokens
 * et/ou €. 0 sur une dimension = désactivée. Partagé par les clés de l'équipe
 * (scope équipe) dans le budget-aware routing. cf. chantier quotas A2b.
 */
export interface ProviderQuota {
    readonly tokensMonthly: number
    readonly eurMonthly: number
}

export interface Team {
    readonly id: string
    readonly tenantId: string
    readonly name: string
    readonly description: string
    /** Route par défaut de l'équipe (chantier Routes B2a-bis). Vide = aucune. */
    readonly routeId?: string
    /**
     * Plafonds mensuels par fournisseur (clé du map = provider_key_id). Compteur
     * partagé par les clés de l'équipe. Absent/vide = aucun cap. cf. quotas A2b.
     */
    readonly providerQuotas?: Record<string, ProviderQuota>
    readonly createdAt: string
}

/** État d'une dimension de quota fournisseur : conso courante vs plafond. */
export interface QuotaDimensionUsage {
    readonly used: number
    readonly limit: number
    /** ok | approaching | exceeded (vide si dimension désactivée). */
    readonly state: string
}

/**
 * ProviderQuotaUsage : consommation du mois calendaire courant d'un
 * fournisseur plafonné de l'équipe (source : compteurs quota backend).
 * Alimente le widget burn rate du dashboard.
 */
export interface ProviderQuotaUsage {
    readonly providerKeyId: string
    readonly tokens: QuotaDimensionUsage
    readonly eur: QuotaDimensionUsage
}

export interface CreateTeamInput {
    readonly name: string
    readonly description?: string
    readonly tenantId?: string
}

export interface UpdateTeamInput {
    readonly name: string
    readonly description: string
}

/** TeamMember : un membre (user humain ou compte de service) d'une équipe. */
export interface TeamMember {
    readonly id: string
    readonly email: string
    readonly name: string
    readonly role: string
    readonly disabled: boolean
}

export type TeamErrorCode =
    | 'invalid_name'
    | 'team_exists'
    | 'team_not_found'
    | 'tenant_mismatch'
    | 'user_not_found'
    | 'tenant_scope'
    | 'route_not_found'
    | 'route_tenant_mismatch'
    | 'team_no_route'
    | 'key_not_in_team'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'invalid_body'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

export class TeamError extends Error {
    readonly code: TeamErrorCode
    readonly httpStatus: number | null
    constructor(code: TeamErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'TeamError'
    }
}

export function isTeamError(err: unknown): err is TeamError {
    return err instanceof TeamError
}

export const KNOWN_TEAM_CODES: readonly TeamErrorCode[] = [
    'invalid_name',
    'team_exists',
    'team_not_found',
    'tenant_mismatch',
    'user_not_found',
    'tenant_scope',
    'route_not_found',
    'route_tenant_mismatch',
    'team_no_route',
    'key_not_in_team',
    'csrf_required',
    'csrf_invalid',
    'unauthorized',
    'forbidden',
    'invalid_body',
    'internal_error',
    'network_error',
    'unknown',
] as const
